<template>
  <div
    class="sticky-pagination-bar bg-surface border-t border-border h-[53px] flex flex-col sm:flex-row items-center justify-between gap-2"
    :class="layoutCollapsedStyle?.value ? 'px-4' : 'px-4 sm:px-6 lg:px-8'"
  >
    <span class="text-xs sm:text-sm text-text-muted">
      Showing {{ startResult }} to {{ endResult }} of {{ totalResults }} results
    </span>
    <div v-if="totalPages > 0" class="flex items-center gap-0.5 sm:gap-1" role="navigation" aria-label="Pagination">
      <button
        type="button"
        :disabled="isFirstPage || disabled"
        aria-label="Previous page"
        class="sticky-pagination-btn sticky-pagination-btn--nav"
        @click="goToPage(currentPage - 1)"
      >
        <i class="ri-arrow-left-s-line text-lg" aria-hidden="true" />
      </button>
      <template v-for="(page, idx) in visiblePages" :key="typeof page === 'string' ? `ellipsis-${idx}` : page">
        <span
          v-if="page === '...'"
          class="sticky-pagination-ellipsis"
          aria-hidden="true"
        >…</span>
        <button
          v-else
          type="button"
          :disabled="disabled"
          :aria-current="page === currentPage ? 'page' : undefined"
          :aria-label="`Page ${page}`"
          :class="[
            'sticky-pagination-btn sticky-pagination-btn--page',
            page === currentPage ? 'sticky-pagination-btn--active' : ''
          ]"
          @click="goToPage(page as number)"
        >
          {{ page }}
        </button>
      </template>
      <button
        type="button"
        :disabled="isLastPage || disabled"
        aria-label="Next page"
        class="sticky-pagination-btn sticky-pagination-btn--nav"
        @click="goToPage(currentPage + 1)"
      >
        <i class="ri-arrow-right-s-line text-lg" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'

const sidebarCollapse = inject<{
  isCollapsed: { value: boolean }
  toggle: () => void
  showToggle: { value: boolean }
} | null>('sidebarCollapse', null)

const layoutCollapsedStyle = inject<{ value: boolean } | null>('layoutCollapsedStyle', null)
const isSidebarCollapsed = computed(() => sidebarCollapse?.isCollapsed?.value ?? false)

interface Props {
  currentPage: number
  totalPages: number
  totalResults: number
  itemsPerPage: number
  maxVisibleButtons?: number
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  maxVisibleButtons: 5,
  disabled: false
})

const emit = defineEmits<{
  'update:currentPage': [page: number]
  'page-changed': [page: number]
}>()

const isFirstPage = computed(() => props.currentPage === 1)
const isLastPage = computed(() => props.currentPage === props.totalPages)

const startResult = computed(() => (props.currentPage - 1) * props.itemsPerPage + 1)
const endResult = computed(() => Math.min(props.currentPage * props.itemsPerPage, props.totalResults))

const visiblePages = computed(() => {
  const pages: (number | string)[] = []
  const { currentPage, totalPages, maxVisibleButtons } = props

  if (totalPages <= maxVisibleButtons) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
  } else {
    const half = Math.floor(maxVisibleButtons / 2)
    let start = Math.max(1, currentPage - half)
    let end = Math.min(totalPages, currentPage + half)

    if (currentPage - half < 1) {
      end = maxVisibleButtons
    }
    if (currentPage + half > totalPages) {
      start = totalPages - maxVisibleButtons + 1
    }

    if (start > 1) {
      pages.push(1)
      if (start > 2) pages.push('...')
    }
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...')
      pages.push(totalPages)
    }
  }

  return pages
})

function goToPage(page: number) {
  if (page >= 1 && page <= props.totalPages && page !== props.currentPage && !props.disabled) {
    emit('update:currentPage', page)
    emit('page-changed', page)
  }
}
</script>

<style scoped>
.sticky-pagination-bar {
  width: 100%;
  box-sizing: border-box;
}

.sticky-pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  min-height: 2rem;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.15s, color 0.15s;
  cursor: pointer;
  border: none;
  background: transparent;
}

@media (min-width: 640px) {
  .sticky-pagination-btn {
    min-width: 2.25rem;
    min-height: 2.25rem;
    width: 2.25rem;
    height: 2.25rem;
  }
}

.sticky-pagination-btn--nav {
  color: var(--color-text-secondary);
}

.sticky-pagination-btn--nav:hover:not(:disabled) {
  background-color: var(--color-surface-hover);
}

.sticky-pagination-btn--nav:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.sticky-pagination-btn--page {
  color: var(--color-text-secondary);
  font-size: 0.75rem;
}

@media (min-width: 640px) {
  .sticky-pagination-btn--page {
    font-size: 0.875rem;
  }
}

.sticky-pagination-btn--page:hover:not(:disabled):not(.sticky-pagination-btn--active) {
  background-color: var(--color-surface-hover);
}

.sticky-pagination-btn--active {
  background-color: var(--color-primary);
  color: var(--color-primary-foreground);
}

.sticky-pagination-btn--active:hover:not(:disabled) {
  background-color: var(--color-primary-hover);
  color: var(--color-primary-foreground);
}

.sticky-pagination-ellipsis {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  height: 2rem;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 500;
}

@media (min-width: 640px) {
  .sticky-pagination-ellipsis {
    min-width: 2.25rem;
    height: 2.25rem;
    font-size: 0.875rem;
  }
}
</style>
